import { getCurrentScope, nextTick, onScopeDispose, ref, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue';

type StorageOptions = {
    mergeDefaults?: boolean;
    onError?: (error: unknown) => void;
};

export function useStorage<T>(key: MaybeRefOrGetter<string>, defaults: MaybeRefOrGetter<T>, storage?: Storage, options: StorageOptions = {}): Ref<T>
{
    const initial = toValue(defaults);
    const data = ref(typeof defaults === 'function' ? initial : defaults) as Ref<T>;
    const onError = options.onError ?? console.error;

    try
    {
        storage ??= typeof window === 'undefined' ? undefined : window.localStorage;
    }
    catch (error)
    {
        onError(error);
    }

    if (!storage)
    {
        return data;
    }

    const backend = storage;
    // Typ wynika z wartości domyślnej: token z null pozostaje surowym tekstem, bez JSON-owych cudzysłowów.
    function serialize(value: T): string
    {
        return initial != null && typeof initial === 'object' ? JSON.stringify(value) : String(value);
    }
    function deserialize(value: string): T
    {
        if (initial != null && typeof initial === 'object')
        {
            return JSON.parse(value);
        }

        if (typeof initial === 'boolean')
        {
            return (value === 'true') as T;
        }

        if (typeof initial === 'number')
        {
            return Number.parseFloat(value) as T;
        }

        return value as T;
    }
    let paused = false;
    function update(event?: StorageEvent)
    {
        if (event && (event.storageArea !== backend || (event.key !== null && event.key !== toValue(key))))
        {
            return;
        }

        if (event?.key === null)
        {
            data.value = initial;

            return;
        }

        paused = true;

        try
        {
            const raw = event ? event.newValue : backend.getItem(toValue(key));

            if (event && raw === serialize(data.value))
            {
                return;
            }

            if (raw == null)
            {
                if (initial != null)
                {
                    backend.setItem(toValue(key), serialize(initial));
                }

                data.value = initial;
            }
            else
            {
                const value = deserialize(raw);

                data.value = !event && options.mergeDefaults && initial != null && typeof initial === 'object' && !Array.isArray(value)
                    ? { ...initial, ...value } : value;
            }
        }
        catch (error)
        {
            onError(error);
        }
        finally
        {
            void nextTick(() => paused = false);
        }
    }

    update();
    paused = false;

    const stop = watch(data, (value) =>
    {
        if (paused)
        {
            return;
        }

        try
        {
            const storageKey = toValue(key);
            const oldValue = backend.getItem(storageKey);
            const newValue = value == null ? null : serialize(value);

            if (newValue !== null && oldValue === newValue)
            {
                return;
            }

            if (newValue !== null)
            {
                backend.setItem(storageKey, newValue);
            }

            if (typeof window !== 'undefined')
            {
                window.dispatchEvent(new StorageEvent('storage', { key: storageKey, oldValue, newValue, storageArea: backend }));
            }

            if (newValue === null)
            {
                backend.removeItem(storageKey);
            }
        }
        catch (error)
        {
            onError(error);
        }
    }, { deep: true });
    const stopKey = watch(() => toValue(key), () => update());

    if (typeof window !== 'undefined')
    {
        window.addEventListener('storage', update, { passive: true });
    }

    if (getCurrentScope())
    {
        onScopeDispose(() =>
        {
            stop();
            stopKey();

            if (typeof window !== 'undefined')
            {
                window.removeEventListener('storage', update);
            }
        });
    }

    return data;
}

export function useLocalStorage<T>(key: MaybeRefOrGetter<string>, defaults: MaybeRefOrGetter<T>, options: StorageOptions = {})
{
    return useStorage(key, defaults, undefined, options);
}
